import { NavHistoryObject } from '@dorian/creation-tools-ui';
import { isMemoryTechnicalStepType } from '../../../../dorian-shared/types/branch/BranchStep';
import { ChoiceMemory } from '../../../../services/choiceMemory/ChoiceMemory';
import { ChoiceMemoryStack } from '../../../../services/choiceMemory/types/ChoiceMemoryStack';

type VariablesSnapshot = ChoiceMemoryStack['variables'];

const controlledRollbackSnapshots = new WeakMap<ChoiceMemory, VariablesSnapshot[]>();

function cloneVariables(variables: VariablesSnapshot | undefined): VariablesSnapshot | undefined {
  if (!variables) return undefined;
  return Object.fromEntries(
    Object.entries(variables).map(([name, variable]) => [name, { ...variable }]),
  ) as VariablesSnapshot;
}

export function pushControlledRollbackSnapshot(
  choiceMemory: ChoiceMemory | undefined,
  variables: VariablesSnapshot | undefined,
) {
  if (!choiceMemory) return;
  const snapshot = cloneVariables(variables);
  if (!snapshot) return;
  const snapshots = controlledRollbackSnapshots.get(choiceMemory) ?? [];
  snapshots.push(snapshot);
  controlledRollbackSnapshots.set(choiceMemory, snapshots);
}

function restoreControlledVariables(
  choiceMemory: ChoiceMemory,
  poppedStackItem: ChoiceMemoryStack | undefined,
  currentStackItem: ChoiceMemoryStack | undefined,
) {
  const snapshots = controlledRollbackSnapshots.get(choiceMemory);
  const rollbackSnapshot = snapshots?.pop();
  const variablesToRestore = rollbackSnapshot ?? currentStackItem?.variables ?? poppedStackItem?.variables;

  if (variablesToRestore) {
    choiceMemory.setCurrentVariables(variablesToRestore);
  }
}

export function clearControlledBackSnapshot(choiceMemory: ChoiceMemory | undefined) {
  if (choiceMemory) {
    controlledRollbackSnapshots.delete(choiceMemory);
  }
}

export function onGoBackStack(
  choiceMemory: ChoiceMemory | undefined,
  historyItem: NavHistoryObject,
  isControlled?: boolean,
): number | 'goAhead' | 'goBack' | undefined {
  if (!choiceMemory) {
    return undefined;
  }
  const poppedStackItem = choiceMemory.pop();
  const currentStackItem = choiceMemory.last;
  if (isControlled) {
    restoreControlledVariables(choiceMemory, poppedStackItem, currentStackItem);
  } else if (currentStackItem?.variables) {
    choiceMemory.setCurrentVariables(currentStackItem.variables);
  }
  const isSameStep = currentStackItem
      && Number(historyItem.branchId) === Number(currentStackItem.step?.branchId)
      && Number(historyItem.episodeId) === Number(currentStackItem.episodeId)
      && Number(historyItem.stepNum) === Number(currentStackItem.stepNum);
  if (isSameStep && isMemoryTechnicalStepType(currentStackItem?.step.stepTypeId)) {
    return 'goBack';
  }
  return undefined;
}
